import { supabase } from '../../../core/lib/supabase';
import type { Source } from '../../../types';
import { processSSEStream, StreamProcessorOptions } from './sseProcessor';
import { configService } from '../../../shared/services/configService';
import { getBackendAuthHeaders } from '../../../shared/services/backendAuthService';

const ACCESS_BACKEND_BASE_URL = process.env.VITE_ACCESS_BACKEND_URL || '';

function buildUrl(baseUrl: string, path: string): string {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

const CHAT_STREAM_ENDPOINT = ACCESS_BACKEND_BASE_URL
  ? buildUrl(ACCESS_BACKEND_BASE_URL, '/multiagent')
  : '';

const DEFAULT_MULTIAGENT_STREAM_IDLE_MS = (() => {
  const raw = process.env.VITE_MULTIAGENT_STREAM_IDLE_MS;
  if (raw == null || raw === '') return 120_000;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 120_000;
})();

export async function createConversation(_workpillId?: string, workpillExecutionId?: string | null) {
  // Usar configuración centralizada
  const config = configService.getConfig();
  
  const conversationData = {
    created_by: config.userId,
    workpill_execution_id: workpillExecutionId || config.executionId
  };

  const { data: conversation, error } = await supabase
    .from('conversations')
    .insert(conversationData)
    .select()
    .single();

  if (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }

  return conversation;
}

export async function addMessage(
  conversationId: string,
  content: string,
  isUser: boolean,
  documentIds?: string[] | null,
  _workpillId?: string | undefined,
  _isEditMode = false,
  _workpillExecutionId?: string | null
) {
  // Usar configuración centralizada
  const config = configService.getConfig();

  // Verify conversation exists and belongs to user
  const { data: conversation, error: conversationError } = await supabase
    .from('conversations')
    .select('id, created_by, workpill_execution_id')
    .eq('id', conversationId)
    .single();

  if (conversationError) {
    console.error('Error verifying conversation:', conversationError);
    throw new Error(`Could not verify conversation: ${conversationError.message}`);
  }

  if (!conversation) {
    throw new Error('Conversation does not exist');
  }

  if (conversation.created_by !== config.userId) {
    throw new Error('You do not have permission to send messages to this conversation');
  }

  // Prepare message data
  const messageData: any = {
    conversation_id: conversationId,
    content,
    is_user: isUser,
    document_id: documentIds?.[0] || null
  };

  // Insert the message
  const { data: message, error } = await supabase
    .from('messages')
    .insert(messageData)
    .select()
    .single();

  if (error) {
    console.error('Error adding message:', error);
    throw error;
  }

  if (!message) {
    console.error('Could not create the message');
    throw new Error('Could not create the message in database');
  }

  return message;
}

export async function updateMessageFeedback(messageId: string, feedback: boolean) {
  const { error } = await supabase
    .from('messages')
    .update({ feedback })
    .eq('id', messageId);

  if (error) throw error;
}

export async function deleteMessage(messageId: string) {
  const config = configService.getConfig();
  
  // Verificar que el mensaje pertenece al usuario
  const { data: message, error: fetchError } = await supabase
    .from('messages')
    .select('id, conversation_id, is_user')
    .eq('id', messageId)
    .single();
  
  if (fetchError || !message) {
    throw new Error('Message not found');
  }
  
  // Verificar que la conversación pertenece al usuario
  const { data: conversation, error: convError } = await supabase
    .from('conversations')
    .select('id, created_by')
    .eq('id', message.conversation_id)
    .single();
  
  if (convError || !conversation) {
    throw new Error('Conversation not found');
  }
  
  if (conversation.created_by !== config.userId) {
    throw new Error('You do not have permission to delete this message');
  }
  
  // Eliminar el mensaje
  const { error: deleteError } = await supabase
    .from('messages')
    .delete()
    .eq('id', messageId);
  
  if (deleteError) {
    throw deleteError;
  }
  
  return true;
}

export async function deleteConversation(conversationId: string) {
  const config = configService.getConfig();
  
  // Verify the conversation exists and belongs to the user
  const { data: conversation, error: conversationError } = await supabase
    .from('conversations')
    .select('id, created_by')
    .eq('id', conversationId)
    .single();

  if (conversationError) {
    console.error('Error verifying conversation:', conversationError);
    throw new Error(`Could not verify conversation: ${conversationError.message}`);
  }

  if (!conversation) {
    throw new Error('Conversation does not exist');
  }

  if (conversation.created_by !== config.userId) {
    throw new Error('You do not have permission to delete this conversation');
  }

  // Delete the conversation (messages will be deleted by CASCADE as confirmed by schema)
  const { error } = await supabase
    .from('conversations')
    .delete()
    .eq('id', conversationId);

  if (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }

  return true;
}

// 🔄 NUEVA FUNCIÓN: Actualizar estado de aplicación de changes
export async function updateChangesApplicationStatus(
  messageId: string,
  status: 'applied' | 'cancelled' | 'accepted' | 'rejected'
) {
  try {
    console.log('🔄 [conversationService] Updating changes state in DB:', { messageId, status });

    if (!messageId) {
      throw new Error('messageId is required to update changes state');
    }

    // Actualizar directamente las columnas dedicadas (sin necesidad de obtener metadata existente)
    const updateData = {
      changes_status: status,
      changes_updated_at: new Date().toISOString()
    };
    
    console.log('🔄 [conversationService] Data to update:', updateData);
    
    const { data, error: updateError } = await supabase
      .from('messages')
      .update(updateData)
      .eq('id', messageId)
      .select('id, changes_status, changes_updated_at');

    if (updateError) {
      console.error('❌ [conversationService] Error updating state:', updateError);
      throw updateError;
    }

    console.log('✅ [conversationService] Changes state updated in DB:', {
      messageId,
      status,
      updatedRows: data?.length || 0,
      verification: data?.[0]
    });

  } catch (error) {
    console.error('❌ Error in updateChangesApplicationStatus:', error);
    throw error;
  }
}

export async function getConversationMessages(conversationId: string) {
  const { data: messages, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('sent_at', { ascending: true });

  if (error) throw error;
  return messages;
}

export async function getUserConversations(workpillExecutionId?: string | null) {
  // Usar configuración centralizada
  const config = configService.getConfig();
  
  // Finding conversations
  
  // Get conversations for the user
  const { data: conversations, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('created_by', config.userId)
    .eq('workpill_execution_id', workpillExecutionId || config.executionId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }

  if (!conversations || conversations.length === 0) {
    return [];
  }

  // Get first user message for each conversation
  const conversationIds = conversations.map(c => c.id);
  
  // Get first user message for each conversation using a window function approach
  // We'll get all user messages ordered by sent_at and then filter to first per conversation
  const { data: firstMessages, error: messagesError } = await supabase
    .from('messages')
    .select('conversation_id, content')
    .in('conversation_id', conversationIds)
    .eq('is_user', true)
    .order('sent_at', { ascending: true });

  if (messagesError) {
    console.error('Error getting first messages:', messagesError);
    // Return conversations without first message if there's an error
    return conversations;
  }

  // Create a map of conversation_id -> first message content
  const firstMessageMap = new Map<string, string>();
  if (firstMessages) {
    firstMessages.forEach(msg => {
      if (!firstMessageMap.has(msg.conversation_id)) {
        firstMessageMap.set(msg.conversation_id, msg.content);
      }
    });
  }

  // Add first message to each conversation
  return conversations.map(conv => ({
    ...conv,
    first_user_message: firstMessageMap.get(conv.id) || null
  }));
}

// ============================================================================
// SSE: backend persiste el mensaje del asistente; el add-in solo refresca desde Supabase
// ============================================================================

async function processChatStreamWithoutDbInsert(
  stream: ReadableStream<Uint8Array>,
  options: StreamProcessorOptions & { signal?: AbortSignal } | undefined
): Promise<{
  id: string;
  content: string;
  sources: Source[];
  changes: string;
  completedThoughts?: any[];
  rawSteps?: any[];
  trace_id?: string;
}> {
  const streamResult = await processSSEStream(
    stream,
    {
      onAgentUpdate: (update) => options?.onAgentUpdate?.(update),
      onTokenUpdate: (agent, token, isFinal, toolResult) =>
        options?.onTokenUpdate?.(agent, token, isFinal, toolResult),
      onAgentChanges: (agent, changes, thought) => {
        if (options?.onAgentChanges) {
          options.onAgentChanges(agent, changes, thought);
        }
      },
      onFinalResponseChunk: (chunkInfo) => options?.onFinalResponseChunk?.(chunkInfo),
      onToken: options?.onToken,
      onError: options?.onError,
      onSources: options?.onSources,
      onChanges: options?.onChanges,
      streamIdleTimeoutMs:
        options?.streamIdleTimeoutMs ?? DEFAULT_MULTIAGENT_STREAM_IDLE_MS,
    },
    true
  );

  const { content, sources, changes, completedThoughts, rawSteps, trace_id } = streamResult;
  const allSteps = rawSteps || completedThoughts || [];
  const finalContent = content || 'Process completed successfully.';
  const pendingId = trace_id ? `pending-${trace_id}` : `pending-${Date.now()}`;

  return {
    id: pendingId,
    content: finalContent,
    sources: sources || [],
    changes: changes || '',
    completedThoughts: allSteps,
    rawSteps: rawSteps || [],
    trace_id
  };
}

export async function fetchMultiagentChatStream(
  conversationId: string,
  content: string,
  opts: {
    isAgentMode: boolean;
    enableWebSearch: boolean;
    enableEuipo?: boolean;
    enableEuipoTmclass?: boolean;
    enableEurlex?: boolean;
    enableCendoj?: boolean;
    enableAepd?: boolean;
    enableOepm?: boolean;
    enableBoe?: boolean;
    enableTc?: boolean;
    enableCnmc?: boolean;
    signal?: AbortSignal;
    workpillExecutionId?: string | null;
  }
): Promise<ReadableStream<Uint8Array>> {
  const config = configService.getConfig();

  const { data: conversation, error: conversationError } = await supabase
    .from('conversations')
    .select('id, created_by, workpill_execution_id')
    .eq('id', conversationId)
    .single();

  if (conversationError) {
    throw new Error(`Could not verify conversation: ${conversationError.message}`);
  }
  if (!conversation) {
    throw new Error('Conversation does not exist');
  }
  if (conversation.created_by !== config.userId) {
    throw new Error('You do not have permission to send messages to this conversation');
  }

  const workpillExecutionId =
    conversation.workpill_execution_id ||
    opts.workpillExecutionId ||
    config.executionId;
  if (!workpillExecutionId) {
    throw new Error('workpill_execution_id is required for multiagent chat');
  }

  if (!CHAT_STREAM_ENDPOINT) {
    throw new Error('Chat stream endpoint not configured (VITE_ACCESS_BACKEND_URL)');
  }

  const requestBody: Record<string, unknown> = {
    conversation_id: conversationId,
    message: content,
    workpill_execution_id: workpillExecutionId,
    workpill_id: config.workpillId ?? undefined,
    skip_history: false,
    enable_document_editing: opts.isAgentMode,
    enable_web_search: opts.enableWebSearch,
    enable_euipo: opts.enableEuipo ?? false,
    enable_euipo_tmclass: opts.enableEuipoTmclass ?? false,
    enable_eurlex: opts.enableEurlex ?? false,
    enable_cendoj: opts.enableCendoj ?? false,
    enable_aepd: opts.enableAepd ?? false,
    enable_oepm: opts.enableOepm ?? false,
    enable_boe: opts.enableBoe ?? false,
    enable_tc: opts.enableTc ?? false,
    enable_cnmc: opts.enableCnmc ?? false,
  };

  const activeEt = config.activeExecutionTemplateId;
  if (activeEt) {
    requestBody.active_execution_template_id = activeEt;
  }

  const headers = await getBackendAuthHeaders(true);
  const response = await fetch(CHAT_STREAM_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
    signal: opts.signal
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Chat stream failed:', response.status, errorText);
    throw new Error(`API request failed: ${response.statusText}`);
  }
  if (!response.body) {
    throw new Error('Response does not contain a readable stream');
  }
  return response.body;
}

export type RunMultiagentChatStreamOptions = StreamProcessorOptions & {
  signal?: AbortSignal;
  isAgentMode: boolean;
  enableWebSearch: boolean;
  enableEuipo?: boolean;
  enableEuipoTmclass?: boolean;
  enableEurlex?: boolean;
  enableCendoj?: boolean;
  enableAepd?: boolean;
  enableOepm?: boolean;
  enableBoe?: boolean;
  enableTc?: boolean;
  enableCnmc?: boolean;
  workpillExecutionId?: string | null;
  /** Override idle timeout ms (SSE stall); omit to use VITE_MULTIAGENT_STREAM_IDLE_MS or 120s */
  streamIdleTimeoutMs?: number;
};

/**
 * POST /multiagent, process SSE. Assistant row is created by access-backend.
 */
export async function runMultiagentChatStream(
  conversationId: string,
  content: string,
  _documentIds: string[] | undefined,
  options: RunMultiagentChatStreamOptions
): Promise<{
  id: string;
  content: string;
  sources: Source[];
  changes: string;
  completedThoughts?: any[];
  rawSteps?: any[];
  trace_id?: string;
}> {
  const stream = await fetchMultiagentChatStream(conversationId, content, {
    isAgentMode: options.isAgentMode,
    enableWebSearch: options.enableWebSearch,
    enableEuipo: options.enableEuipo,
    enableEuipoTmclass: options.enableEuipoTmclass,
    enableEurlex: options.enableEurlex,
    enableCendoj: options.enableCendoj,
    enableAepd: options.enableAepd,
    enableOepm: options.enableOepm,
    enableBoe: options.enableBoe,
    enableTc: options.enableTc,
    enableCnmc: options.enableCnmc,
    signal: options.signal,
    workpillExecutionId: options.workpillExecutionId
  });
  return processChatStreamWithoutDbInsert(stream, options);
}

type MessageRowForRedline = {
  is_user?: boolean;
  sent_at?: string;
  metadata?: { edited_documents?: unknown };
};

function messageSentAfterCutoff(sentAt: string | undefined, since: Date): boolean {
  if (!sentAt) return false;
  const t = new Date(sentAt).getTime();
  if (Number.isNaN(t)) return false;
  return t > since.getTime();
}

function findRedlineEntryForActiveTemplate(
  rows: MessageRowForRedline[],
  activeId: string,
  since?: Date
): { storage_path: string } | null {
  const aiNewestFirst = [...rows]
    .filter((m) => {
      if (m.is_user) return false;
      if (since && !messageSentAfterCutoff(m.sent_at, since)) return false;
      return true;
    })
    .reverse();
  for (const msg of aiNewestFirst) {
    const edited = msg.metadata?.edited_documents;
    if (!Array.isArray(edited)) continue;
    const entry = edited.find(
      (d: {
        execution_template_id?: string;
        storage_path?: string;
        redline_compare?: boolean;
      }) =>
        d.execution_template_id &&
        String(d.execution_template_id).toLowerCase() === activeId.toLowerCase() &&
        Boolean(d.storage_path) &&
        d.redline_compare === true
    );
    if (entry?.storage_path) return { storage_path: entry.storage_path };
  }
  return null;
}

const REDLINE_POLL_ATTEMPTS = 14;
const REDLINE_POLL_MS = 450;

/**
 * After the stream, load Aspose redline .docx when backend persisted redline_compare for **this turn**.
 * `since`: only consider assistant messages with sent_at after this (e.g. user message sent_at) so follow-up
 * turns do not re-download stale redlines from earlier edits.
 */
export async function loadRedlineDocumentIfPresent(
  conversationId: string,
  since?: Date
): Promise<boolean> {
  const cfg = configService.getConfig();
  const activeId = cfg.activeExecutionTemplateId;
  if (!activeId) return false;

  for (let attempt = 0; attempt < REDLINE_POLL_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, REDLINE_POLL_MS));
    }
    const rows = await getConversationMessages(conversationId);
    const hit = findRedlineEntryForActiveTemplate(rows, activeId, since);
    if (!hit) continue;

    const { data, error } = await supabase.storage
      .from('execution-templates')
      .download(hit.storage_path);

    if (error || !data) {
      console.warn('[conversationService] Redline download failed:', error?.message);
      return false;
    }

    const { WordService } = await import('../../../shared/services/wordService');
    await WordService.loadDocumentFromBlob(data);
    return true;
  }

  return false;
}

